import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { SUB_MENU_LIST } from 'components/global/Sidebar/constants';
import { SubMenuList } from 'components/global/Sidebar/constants/SubMenuList.constants';
import { LMS_ROUTE_BASE } from 'routes/LmsRoutes';
import { RootState } from 'store/rootReducers';

export const APP_TITLE = 'Nimbly';

/**
 * Top-level sections, labelled like their main-sidebar entry. Deeper pages take their title from
 * the sub-sidebar menu (SUB_MENU_LIST) instead, so a page added to the sidebar gets a title
 * without touching this file.
 */
const SECTION_TITLE_KEYS: Record<string, string> = {
	'/home': 'nav.home',
	'/analytics': 'nav.dashboard',
	'/reportsHub': 'nav.reportsHub',
	'/issues': 'nav.issuetracker',
	'/gallery': 'nav.gallery',
	[LMS_ROUTE_BASE]: 'nav.nimblyLearn',
	'/repository': 'nav.repository',
	'/admin': 'nav.manage',
	'/settings': 'nav.settings',
	'/superadmin': 'nav.superadmin',
};

type FeatureAccess = Parameters<typeof SUB_MENU_LIST>[1];

const getTitleEntries = (featureAccess?: FeatureAccess, isSandboxAccount = false): [string, string][] => {
	const entries = Object.entries(SECTION_TITLE_KEYS);

	// Typed loosely at the source (the sandbox branch builds it as `any`).
	// Same arguments as SubSidebar, so sandbox accounts get titles from the menu they actually see.
	const subMenuList: SubMenuList = SUB_MENU_LIST(isSandboxAccount, featureAccess);

	Object.values(subMenuList).forEach((menus) => {
		menus?.forEach((menu) => {
			entries.push([menu.path, menu.label]);
			menu.collapsibleList?.forEach((item) => {
				entries.push([item.path, item.label]);
				if (item.subPage) entries.push([item.subPage, item.label]);
			});
		});
	});

	return entries;
};

/**
 * i18n key (or literal label) of the deepest menu entry whose path is `pathname` or one of its
 * ancestors, so `/admin/sites/abc` resolves to Sites. On a tie the later, more specific entry
 * wins: a collapsible group shares its path with its first child.
 */
export const getRouteTitleKey = (
	pathname: string,
	featureAccess?: FeatureAccess,
	isSandboxAccount = false,
): string | undefined => {
	let best: [string, string] | undefined;

	getTitleEntries(featureAccess, isSandboxAccount).forEach((entry) => {
		const [path] = entry;
		const matches = pathname === path || pathname.startsWith(`${path}/`);
		if (matches && (!best || path.length >= best[0].length)) best = entry;
	});

	return best?.[1];
};

export const formatDocumentTitle = (pageTitle?: string) => (pageTitle ? `${pageTitle} · ${APP_TITLE}` : APP_TITLE);

/** Keeps `document.title` in step with the current route ("Issues · Nimbly"). */
export function useDocumentTitle() {
	const { t } = useTranslation();
	const pathname = useSelector((state: RootState) => state.router.location.pathname);
	const featureAccess = useSelector((state: RootState) => state.featureAccess.features);
	const isSandboxAccount = useSelector((state: RootState) => state.jwtauth.isSandboxAccount);

	const titleKey = useMemo(
		() => getRouteTitleKey(pathname, featureAccess, isSandboxAccount),
		[pathname, featureAccess, isSandboxAccount],
	);

	useEffect(() => {
		document.title = formatDocumentTitle(titleKey ? t(titleKey) : undefined);
	}, [titleKey, t]);
}
